import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

import { type ClientSector, clientSectorGroups } from '@/content/clients';

import { allSectorsLabel, sectorGroupLabel } from '../content';
import { type SectorFilter } from '../hooks/useClientRoster';

type Props = {
  sector: SectorFilter;
  onSelect: (sector: ClientSector) => void;
  onClear: () => void;
};

/** Softens both ends of the phone rail, so a chip reads as continuing off-screen. */
const EDGE_FADE = 'linear-gradient(90deg, transparent 0, #000 4%, #000 96%, transparent 100%)';

/**
 * The row's layout, which is two different rows either side of `md`.
 *
 * Sixteen chips wrapped on a 390px screen is eleven rows of them — about 700px
 * of filter standing between the heading and the first client, on the page
 * whose entire job is to show clients. Measured, not guessed: that is what the
 * wrapped row actually did on a phone.
 *
 * So below `md` the chips are one horizontally scrollable rail. It costs about
 * 48px, it is the pattern a phone user already knows from every catalogue app,
 * and the edge fade is what says there is more of it to the right. Keyboard
 * users are unaffected — Tab still reaches every chip and the browser scrolls
 * each one into view as it does.
 *
 * From `md` the width exists, so the chips wrap and centre and every sector is
 * visible at once, which is the better row when it fits.
 *
 * `scroll-snap` on the rail stops a flick leaving a chip half cut off, and
 * `pb` leaves room for the scrollbar on the platforms that reserve space for
 * one, so it cannot sit on top of the chips.
 */
const railSx = {
  flexWrap: { xs: 'nowrap', md: 'wrap' },
  justifyContent: { md: 'center' },
  gap: 2,
  overflowX: { xs: 'auto', md: 'visible' },
  pb: { xs: 2, md: 0 },
  scrollSnapType: { xs: 'x proximity', md: 'none' },
  maskImage: { xs: EDGE_FADE, md: 'none' },
  WebkitMaskImage: { xs: EDGE_FADE, md: 'none' },
  // The rail is the scroller, so it must not be clipped by the band's padding;
  // the negative margin lets the first and last chip reach the screen edge and
  // the padding puts them back where the text column starts.
  mx: { xs: -4, md: 0 },
  px: { xs: 4, md: 0 },
  '& .MuiChip-root': { scrollSnapAlign: 'center', flex: '0 0 auto' },
  // A visible scrollbar under a single row of chips reads as a broken element.
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
} as const;

/**
 * One row of chips: which sector to show.
 *
 * ## They are buttons in a list, not a tablist
 *
 * The same reasoning as `DeliveryFilterChips`, which this follows deliberately
 * — a visitor should not meet two different filter idioms on one site. A
 * `tablist` promises arrow-key navigation between tabs and a tab panel for
 * each, and this is one list being narrowed, so the role would be a lie a
 * screen reader then holds us to. They are `aria-pressed` toggle buttons in a
 * labelled group, which is what they are.
 *
 * MUI's `Chip` renders a `<div>` by default; `component="button"` with
 * `clickable` is what makes each one a real control that Tab reaches and the
 * space bar fires.
 *
 * It is one scrollable rail on a phone and a wrapped, centred row from `md` —
 * see `railSx` for the measurement that forced the split.
 *
 * ## Counts on every chip
 *
 * "Banking & Insurance (10)" tells a reader whether the tap is worth making,
 * and — on a page whose entire argument is how many organisations there are —
 * the counts are the argument, restated fifteen times. They are counted off
 * the same groups the grid draws, so a chip here can never lead to an empty
 * grid.
 */
export function ClientSectorChips({ sector, onSelect, onClear }: Props) {
  return (
    <Stack direction="row" role="group" aria-label={sectorGroupLabel} sx={railSx}>
      <Chip
        component="button"
        clickable
        label={allSectorsLabel}
        aria-pressed={sector === null}
        color={sector === null ? 'primary' : 'default'}
        variant={sector === null ? 'filled' : 'outlined'}
        onClick={onClear}
      />
      {clientSectorGroups.map((group) => {
        const on = sector === group.sector;
        return (
          <Chip
            key={group.sector}
            component="button"
            clickable
            label={`${group.sector} (${group.clients.length})`}
            aria-pressed={on}
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            // Tapping the chip that is already on clears it, which is the only
            // way back to every sector on a phone without hunting for the
            // first chip again.
            onClick={() => (on ? onClear() : onSelect(group.sector))}
          />
        );
      })}
    </Stack>
  );
}
